import './index.css'

// import SortOptionsView from './sort-options/view'
import cn from 'clsx'
import debounce from 'lodash/debounce'
import type { DebouncedFunc } from 'lodash'
import differenceWith from 'lodash/differenceWith'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { PHONE } from '@disco/constants/DeviceTypes'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import {
  defaultSortTypes,
  SLIDER_DURATION_RANGE,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
} from '@disco/constants/SortTypes'
import type {
  SimSearchQuery,
  State as SearchState,
} from '@disco/state-management/reducers/search'
import { keyEqualTo } from '@disco/utils/keyboard'
import { checkSetFilters, getFiltersWithValidValues } from '@disco/utils/search'

import type { SortFields } from 'types/explorer'
import type { UserSettings } from 'types/user'

import { DiscoCallout } from 'components/ds'
import SidebarSlider from 'components/sidebar/sidebar-slider'
import { Button, ButtonContainer, Input } from 'components/ui'
import { RadioButton, RadioGroup } from 'components/ui/radio'

import DateFilter from './date-filter'
import FilterRow from './filter-row'
import GroupFilters from './groups'
import MetadataFilters from './metadata'
import NativeTagsFilter from './native-tags-filter'
import TagsFilter from './tags'

const searchTypes = [
  {
    value: false,
    text: 'All',
  },
  {
    value: 'track',
    text: 'Tracks',
  },
  {
    value: 'playlist',
    text: 'Playlists',
  },
  {
    value: 'album',
    text: 'Albums',
  },
  {
    value: 'artist',
    text: 'Artists',
  },
]
const defaultSortDirections = {
  name: SORT_DIRECTION_ASC,
  date: SORT_DIRECTION_DESC,
  relevance: SORT_DIRECTION_DESC,
}
type OwnProps = SearchState & {
  searchOpen: boolean
  sidebarOpen: boolean
  mobileDeviceType?: string
  app: Record<string, any>
  updateSearchParams: (...args: Array<any>) => any
  getSearchResults: (...args: Array<any>) => any
  settings: UserSettings
  updateUserSettingsUnsafe: (...args: Array<any>) => any
  pushUINotification: (...args: Array<any>) => any
  nativeTagsEnabled: boolean
  albumsEnabled: boolean
  showcasesEnabled: boolean
  searchQuery: {
    simSearch: SimSearchQuery
  }
  simSearch: SimSearchQuery
  simSearchEnabled: boolean
  aimsEnabled: boolean
  chosenColumns: SortFields[]
  windowWidth?: any
  clearSearch: (...args: Array<any>) => any
  addMetadataFilter: (...args: Array<any>) => any
  removeMetadataFilter: (...args: Array<any>) => any
  focusOnExplorer: (...args: Array<any>) => any
  loadPaneWithSearchResults: (...args: Array<any>) => any
  baseSearchGroups: Record<string, any>[]
  searchGroups: Record<string, any>[]
  searchGroupsIndexed: (...args: Array<any>) => any
  explorerFirstPane: null | Record<string, any>
}
type OwnState = {
  focused: boolean
  searchTerm: string
  showFilters: boolean
  showAllSearchGroups: boolean
}

class SearchFilters extends React.Component<OwnProps, OwnState> {
  state: OwnState = {
    focused: false,
    searchTerm: this.props.searchTerm ?? '',
    showFilters: false,
    showAllSearchGroups: false,
  }
  _filters: HTMLDivElement | null | undefined
  _searchInput: HTMLElement | null | undefined
  _container: HTMLDivElement | null | undefined
  _filtersSnapshot: OwnProps | null = null
  _refreshSearchResults: DebouncedFunc<() => any> = debounce(
    () => this.props.getSearchResults(),
    1200,
  )

  UNSAFE_componentWillReceiveProps(nextProps: OwnProps) {
    // sync input text when the store term changes outside this component
    // (e.g. clearSearch from a track-pane tag click); a change caused by
    // typing here already matches local state, so this never fires for it
    if (
      nextProps.searchTerm !== this.props.searchTerm &&
      nextProps.searchTerm !== this.state.searchTerm
    ) {
      this._setSearchTerm.cancel()
      this.setState({ searchTerm: nextProps.searchTerm ?? '' })
    }

    const isDifferent = this._checkDifference(nextProps)

    if (isDifferent) {
      const termClear =
        !Boolean(nextProps.searchTerm) && Boolean(this.props.searchTerm)

      // clear search if:
      // 1. user removes text in main search input and types something new
      if (termClear && !this._checkSetFilters(nextProps)) {
        this.props.clearSearch(true)
      }

      if (
        nextProps.searchOpen &&
        !this.state.showFilters &&
        (this.props.sort !== nextProps.sort ||
          this.props.sortDirection !== nextProps.sortDirection ||
          !isEqual(this.props.nativeTags, nextProps.nativeTags))
      ) {
        this._refreshSearchResults()
      }
    }

    if (nextProps.windowWidth !== this.props.windowWidth) {
      this._updateSearchHeight()
    }

    // debounce update for 0.2 sec till animation completes and we can get
    // actual height
    if (
      nextProps.sidebarOpen !== this.props.sidebarOpen ||
      nextProps.searchCompact !== this.props.searchCompact
    ) {
      setTimeout(() => this._updateSearchHeight(), 250)
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this._handleKeyDown)
    document.addEventListener('mousedown', this._handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this._handleKeyDown)
    document.removeEventListener('mousedown', this._handleClickOutside)
    this._setSearchTerm.cancel()
  }

  _handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value
    this.setState({ searchTerm }, () => {
      if (!searchTerm) {
        this._setSearchTerm.cancel()
        if (this._checkSetFilters()) {
          this.props.updateSearchParams({ searchTerm: '', searchOpen: true })
          this._refreshSearchResults()
        } else {
          this._refreshSearchResults.cancel()
          this.setState({ showFilters: false })
          this.props.updateSearchParams({ searchTerm: '', searchOpen: false })
        }
        return
      }
      this._setSearchTerm({
        searchTerm,
        searchOpen: this._checkSetFilters(),
      })
    })
  }
  _setSearchTerm: DebouncedFunc<
    (params: { searchOpen: boolean; searchTerm: string }) => void
  > = debounce((params: { searchTerm: string; searchOpen: boolean }) => {
    this.props.updateSearchParams(params)
    this.props.getSearchResults()
  }, 1200)
  _putCursor: () => void = () => {
    if (!this._searchInput) return

    this._searchInput.focus()
  }
  _handleInputFocus: () => void = () => {
    this.setState({
      focused: true,
    })
    if (this.props.mobileDeviceType === PHONE) this.props.focusOnExplorer()
    this.props.updateSearchParams({
      searchOpen: true,
    })
    const { list, searchTerm, nextPage, explorerFirstPane } = this.props

    if (list) {
      // TODO: create searchPaneTitle in rendering, not in dispatch()
      let searchPaneTitle = searchTerm
        ? `Search Results for ${searchTerm}`
        : 'Search Results'

      if (this.props.searchQuery?.simSearch) {
        searchPaneTitle = `Similar tracks`
      }

      // open existing search results if user searched something earlier
      // unless search results are already opened
      if (
        !explorerFirstPane ||
        explorerFirstPane.type !== SEARCH_RESULTS_PANE ||
        explorerFirstPane.groupId
      ) {
        this.props.loadPaneWithSearchResults({
          index: 0,
          name: searchPaneTitle,
          type: SEARCH_RESULTS_PANE,
          simSearch: this.props.searchQuery?.simSearch,
          next: nextPage,
        })
      }
    }
  }
  _handleInputBlur: () => void = () => {
    this.setState({
      focused: false,
    })
  }
  _toggleSearchGroups: () => void = () =>
    this.setState({
      showAllSearchGroups: !this.state.showAllSearchGroups,
    })

  _checkDifference(
    nextProps: OwnProps,
    prevProps: OwnProps = this.props,
  ): boolean {
    // exclude from filter diff values that not yet set by user,
    // so we won't trigger update because user added but not completed
    // editing new filter value
    const newFiltersDiff = differenceWith(
      nextProps.filters,
      prevProps.filters,
      isEqual,
    ).filter(
      filter => filter.key && (filter.value || filter.start || filter.end),
    )
    const deletedFiltersDiff = differenceWith(
      prevProps.filters,
      nextProps.filters,
      isEqual,
    ).filter(
      filter => filter.key && (filter.value || filter.start || filter.end),
    )
    const endDate = prevProps.endDate ? prevProps.endDate.getTime() : null
    const nextEndDate = nextProps.endDate ? nextProps.endDate.getTime() : null
    return (
      prevProps.searchTerm !== nextProps.searchTerm ||
      prevProps.type !== nextProps.type ||
      newFiltersDiff.length > 0 ||
      deletedFiltersDiff.length > 0 ||
      prevProps.groups !== nextProps.groups ||
      prevProps.tags !== nextProps.tags ||
      !isEqual(prevProps.nativeTags, nextProps.nativeTags) ||
      prevProps.sort !== nextProps.sort ||
      prevProps.sortDirection !== nextProps.sortDirection ||
      prevProps.startDate?.getTime() !== nextProps.startDate?.getTime() ||
      endDate !== nextEndDate ||
      prevProps.duration !== nextProps.duration
    )
  }

  _handleKeyDown: (evt: KeyboardEvent) => void = (evt: KeyboardEvent) => {
    if (
      this.state.showFilters ||
      this._searchInput === document.activeElement
    ) {
      if (keyEqualTo(evt, 'Escape')) {
        if (this._searchInput) {
          this._searchInput.blur()
        }

        this._clearSearch()
      }

      if (keyEqualTo(evt, 'Enter')) {
        const target = evt.target as Node | null
        const isEditingPanelField =
          Boolean(target) &&
          Boolean(this._filters?.contains(target)) &&
          Boolean((target as HTMLElement).closest('input, textarea, select'))
        if (this.state.showFilters) {
          if (!isEditingPanelField) {
            this._closeFilters()
          }
        } else if (this._searchInput === document.activeElement) {
          // A debounced search is only pending right after the user types.
          // When the term was pre-filled from an earlier search and the user
          // just refocuses, nothing is pending so flush() would no-op — run
          // the search directly so Enter always re-runs the current search
          // and reopens the results.
          if (this._checkSetFilters()) {
            this._setSearchTerm.cancel()
            this.props.updateSearchParams({
              searchTerm: this.state.searchTerm,
              searchOpen: true,
            })
            this.props.getSearchResults()
          }
        }
      }
    }
  }
  _openFilters: () => void = () => {
    if (!this._filtersSnapshot) {
      this._filtersSnapshot = this.props
    }
    this.setState({
      showFilters: true,
    })
  }
  _closeFilters: () => void = () => {
    this.setState({
      showFilters: false,
    })
    if (
      !this._filtersSnapshot ||
      this._checkDifference(this.props, this._filtersSnapshot)
    ) {
      this._refreshSearchResults()
    }
    this._filtersSnapshot = null
  }
  _toggleFilters: () => void = () => {
    const opening = !this.state.showFilters
    if (opening && !this._filtersSnapshot) {
      this._filtersSnapshot = this.props
    }
    this.setState({
      showFilters: opening,
    })

    this._handleInputFocus()
  }
  // React portals (e.g. the metadata date picker calendar) render outside
  // _container in the DOM, so a contains() check alone reads clicks on them
  // as outside. Their synthetic events still bubble through the React tree,
  // so the container's onMouseDown sees them first and marks the event.
  _insideMouseDown: Event | null = null
  _markInsideMouseDown: (evt: React.MouseEvent) => void = evt => {
    this._insideMouseDown = evt.nativeEvent
  }
  _handleClickOutside: (evt: MouseEvent) => void = evt => {
    const wasInside =
      evt === this._insideMouseDown ||
      Boolean(this._container?.contains(evt.target as Node))
    this._insideMouseDown = null
    if (this.state.showFilters && !wasInside) {
      this.setState({
        showFilters: false,
      })
    }
  }
  _clearSearch: () => void = () => {
    this._setSearchTerm.cancel()
    this.props.clearSearch()
    this._filtersSnapshot = null
    this.setState({
      searchTerm: '',
      showFilters: false,
    })
  }
  _clickClearSearch: () => void = () => {
    this._clearSearch()

    this._putCursor()

    if (this._searchInput) {
      this._searchInput.blur()
    }
  }

  _getDuration(duration: string): number[] {
    return duration.split('-').map(d => Number(d))
  }

  _checkSetFilters(nextProps?: OwnProps): boolean {
    const props = nextProps || this.props
    return checkSetFilters({
      searchTerm: this.state.searchTerm ?? props.searchTerm,
      type: props.type,
      groups: props.groups,
      tags: props.tags,
      nativeTags: props.nativeTags,
      startDate: props.startDate,
      duration: props.duration,
      filters: props.filters,
    })
  }

  _renderFilters(): null | React.ReactNode {
    if (this.props.mobileDeviceType === PHONE) return null
    const changeFilters = !this.state.showFilters ? (
      <div className="SearchResults_Filters_Change">
        <Button caption="Refine search..." onClick={this._openFilters} />
      </div>
    ) : null
    const filtersClassName = cn('SearchResults_Filters', {
      'show-filters': this.state.showFilters,
      'compact-view': this.props.searchCompact,
    })
    const contentClassName = cn('SearchResults_Content', {
      'filters-opened': this.state.showFilters,
    })
    const searchClass = cn('SearchResults', {
      'is-active': this.props.searchOpen,
      'is-sidebar': this.props.sidebarOpen,
      'is-expanded': this.state.showFilters,
    })
    const searchCaptionClass = cn('SearchResults_SearchTerm', {
      'is-expanded': this.state.showFilters,
    })
    const dateFilter =
      this.props.mobileDeviceType !== PHONE ? (
        <DateFilter
          disabled={this.props.type === 'artist'}
          dateStart={this.props.startDate}
          dateEnd={this.props.endDate}
          updateSearchParams={this.props.updateSearchParams}
          scrollContainer={this._filters}
        />
      ) : null

    const sliderDurationRange = this._getDuration(SLIDER_DURATION_RANGE)

    const searchPane = (
      <div className={searchClass}>
        <div className={contentClassName}>
          {changeFilters}
          <div
            className={filtersClassName}
            ref={r => {
              this._filters = r
            }}
          >
            {this.state.showFilters && (
              <>
                {this.props.aimsEnabled ? (
                  <DiscoCallout
                    type="feature"
                    size="1"
                    mt="2"
                    dismissible
                    dismissKey="aims_search_banner_dismissed"
                  >
                    <strong>Try AIMS Search for a new experience.</strong>{' '}
                    Access it{' '}
                    <RouterLink
                      to="/search"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      here
                    </RouterLink>
                    .
                  </DiscoCallout>
                ) : (
                  <DiscoCallout
                    type="feature"
                    size="1"
                    mt="2"
                    dismissible
                    dismissKey="new_search_page_banner_dismissed"
                  >
                    <strong>
                      We have improved DISCO Search. Better filtering and more
                      metadata at a glance.
                    </strong>{' '}
                    Access it{' '}
                    <RouterLink
                      to="/search"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      here
                    </RouterLink>
                    .
                  </DiscoCallout>
                )}
                <MetadataFilters
                  updateSearchParams={this.props.updateSearchParams}
                  addMetadataFilter={this.props.addMetadataFilter}
                  removeMetadataFilter={this.props.removeMetadataFilter}
                  filters={this.props.filters}
                  metadataKeys={this.props.metadataKeys}
                  mobileDeviceType={this.props.mobileDeviceType}
                  disabled={
                    this.props.type === 'artist' ||
                    this.props.type === 'album' ||
                    this.props.type === 'playlist'
                  }
                />
                <TagsFilter
                  chosenTags={this.props.tags}
                  isPhone={this.props.mobileDeviceType === PHONE}
                  updateSearchParams={this.props.updateSearchParams}
                  scrollContainer={this._filters}
                  disabled={this.props.type === 'artist'}
                />
                {this.props.nativeTagsEnabled && (
                  <NativeTagsFilter
                    chosenTags={this.props.nativeTags}
                    updateSearchParams={this.props.updateSearchParams}
                    isPhone={this.props.mobileDeviceType === PHONE}
                    scrollContainer={this._filters}
                    disabled={this.props.type === 'artist'}
                  />
                )}
                {dateFilter}
                <GroupFilters
                  groups={this.props.groups}
                  searchGroupsIndexed={this.props.searchGroupsIndexed}
                  searchGroups={this.props.searchGroups}
                  baseSearchGroups={this.props.baseSearchGroups}
                  updateSearchParams={this.props.updateSearchParams}
                  showAllSearchGroups={this.state.showAllSearchGroups}
                  toggleMore={this._toggleSearchGroups}
                  isPhone={this.props.mobileDeviceType === PHONE}
                  scrollContainer={this._filters}
                  disabled={this.props.type === 'artist'}
                />
                <FilterRow caption="Filter by type:" reducedMargins>
                  <div className="SearchResults_FilterOption is-active SearchResults_FilterOption_Expandable_AlignTop">
                    Show
                  </div>
                  {this._renderSearchFilterOptions()}
                </FilterRow>
                <SidebarSlider
                  disabled={this.props.type === 'artist'}
                  key="SidebarSlider"
                  label={
                    <span
                      className={cn('SearchResults_FilterOption', {
                        'is-active':
                          this.props.duration === SLIDER_DURATION_RANGE,
                      })}
                    >
                      Duration
                    </span>
                  }
                  sliderRange={sliderDurationRange}
                  formatter={v => {
                    if (v === sliderDurationRange[0]) return '0:00'
                    if (v === sliderDurationRange[1]) return '∞'
                    let mm = Math.floor(v / 60)
                    let ss = (v % 60).toString().padStart(2, '0')
                    return `${mm}:${ss}`
                  }}
                  step={30}
                  val={
                    this.props.duration
                      ? this._getDuration(this.props.duration).map(
                          (value, index) =>
                            value === 0
                              ? sliderDurationRange[index] ?? value
                              : value,
                        )
                      : sliderDurationRange
                  }
                  setVal={duration => {
                    const updateDuration = duration.map((value, index) =>
                      value === sliderDurationRange[index] ? null : value,
                    )
                    const hasValues = updateDuration.some(
                      value => value !== null,
                    )
                    this.props.updateSearchParams({
                      duration: hasValues ? updateDuration.join('-') : null,
                      type: 'track',
                    })
                  }}
                  constantlyActive
                  showFilters={this.state.showFilters}
                />
              </>
            )}
          </div>
          {/* TODO: clean-up SortOptionsView */}
          {/* <SortOptionsView */}
          {/*   onChange={this._changeSort} */}
          {/*   onSortSelect={this._toggleSortColumn} */}
          {/*   sort={this.props.sort} */}
          {/*   sortColumns={this.props.chosenColumns} */}
          {/*   sortDirection={this.props.sortDirection} */}
          {/*   className={resultsClassName} */}
          {/* /> */}
          {this.state.showFilters && (
            <ButtonContainer
              className="SearchResults_Filters_ButtonsContainer"
              alignRight
            >
              <Button onClick={this._clearSearch} caption="Cancel" />
              <Button
                onClick={this._closeFilters}
                caption="Show results"
                appearance="primary"
              />
            </ButtonContainer>
          )}
        </div>
      </div>
    )

    const hasFilters = this._checkSetFilters()

    const noAdvancedFilters = Boolean(this.props.searchQuery?.simSearch)
    return (
      <>
        {hasFilters && (
          <div
            className="SearchResults_Close"
            style={{
              right: noAdvancedFilters ? 0 : undefined,
            }}
          >
            <Button
              appearance="transparent"
              icon="close"
              tooltip="Clear"
              onClick={this._clickClearSearch}
            />
          </div>
        )}
        {this.props.mobileDeviceType !== PHONE && !noAdvancedFilters && (
          <div className={searchCaptionClass}>
            <Button
              appearance="transparent"
              icon="arrow-down"
              tooltip="Advanced search"
              onClick={this._toggleFilters}
            />
          </div>
        )}
        {searchPane}
      </>
    )
  }

  _toggleCompactMode: () => void = () => {
    this.props.updateSearchParams({
      searchCompact: !this.props.searchCompact,
    })
  }
  _updateSearchHeight: () => any | void = () => {
    // we had to calculate offset for absolutely positioned element,
    // becase filters can vary in size
    if (
      this.props.app.mobileDeviceType !== PHONE &&
      this.props.searchCompact &&
      this.props.searchOpen
    ) {
      return this.props.updateSearchParams({
        searchHeight: '3.95rem',
      })
    }

    let height: string | null = '11.1rem'

    if (this.props.app.mobileDeviceType !== PHONE && this._filters) {
      height = `calc(6.2rem + ${this._filters.clientHeight}px)`
    }

    height = this.props.searchOpen ? height : null
    this.props.updateSearchParams({
      searchHeight: height,
    })
  }

  componentDidUpdate(prevProps: OwnProps, prevState: OwnState) {
    const needsUpdate =
      prevState.showAllSearchGroups !== this.state.showAllSearchGroups ||
      !this.props.searchHeight ||
      prevProps.filters.length !== this.props.filters.length ||
      prevProps.groups !== this.props.groups ||
      prevProps.tags !== this.props.tags
    if (this.props.searchOpen && needsUpdate) this._updateSearchHeight()
  }

  _renderSearchFilterOptions(): React.ReactNode {
    const disablePlaylistOptions =
      getFiltersWithValidValues(this.props.filters).length > 0
    const { groups, tags, nativeTags, startDate, duration } = this.props
    const disableArtistOption = Boolean(
      disablePlaylistOptions ||
        groups.length ||
        tags.length ||
        nativeTags.length ||
        startDate.getTime() ||
        duration !== null,
    )
    return (
      <RadioGroup
        className="radio-group"
        value={this.props.type}
        onChange={type =>
          this.props.updateSearchParams({
            type: type,
          })
        }
      >
        {searchTypes
          .filter(type => {
            if (type.value === 'artist') {
              return this.props.showcasesEnabled
            }

            if (type.value === 'album') {
              return this.props.albumsEnabled
            }

            return true
          })
          .map(type => {
            let filter = type.value
            let disabled = false
            let tooltip

            switch (filter) {
              case 'playlist':
              case 'album':
                disabled = disablePlaylistOptions
                tooltip = disablePlaylistOptions
                  ? `To use the ${filter} filter, clear the "Filter by" field.`
                  : null
                break

              case 'artist':
                disabled = disableArtistOption
                tooltip = disableArtistOption
                  ? 'To use the artist filter, clear the other search terms.'
                  : null
                break
            }

            return (
              <RadioButton
                key={type.text}
                tooltip={tooltip}
                className="SearchResults_FilterOption"
                value={filter}
                label={type.text}
                disabled={disabled}
              />
            )
          })}
      </RadioGroup>
    )
  }

  _toggleSortColumn: (sortParam: SortFields) => void = (
    sortParam: SortFields,
  ) => {
    // we don't allow hiding default sorting methods
    if (defaultSortTypes.includes(sortParam)) {
      this.props.pushUINotification({
        message: "Sorry! this filter can't be removed.",
        level: 'error',
      })
      return
    }

    const { chosenColumns } = this.props
    let columns: SortFields[] = []

    if (chosenColumns.includes(sortParam)) {
      columns = chosenColumns.filter(c => c !== sortParam)

      // reset to default value if user hides currently chosen sort column
      if (this.props.sort === sortParam) {
        this.props.updateSearchParams({
          sort: 'score',
        })
      }
    } else {
      columns = [...chosenColumns, sortParam]
    }

    this.props.updateUserSettingsUnsafe({
      settings: {
        search_columns: columns,
      },
    })
  }
  _changeSort: (sortParam: string) => any = (sortParam: string) => {
    const { sort, sortDirection, updateSearchParams } = this.props

    if (sort === sortParam) {
      const newSortDirection =
        sortDirection === SORT_DIRECTION_ASC
          ? SORT_DIRECTION_DESC
          : SORT_DIRECTION_ASC
      return updateSearchParams({
        sort: sortParam,
        sortDirection: newSortDirection,
      })
    }

    return updateSearchParams({
      sort: sortParam,
      sortDirection: defaultSortDirections[sortParam],
    })
  }

  render(): React.ReactElement<'div'> {
    return (
      <div
        className="SearchFilters_Cont"
        onMouseDown={this._markInsideMouseDown}
        ref={r => {
          this._container = r
        }}
      >
        <div
          className={cn('topbar-search', {
            focused: this.state.focused,
          })}
          onClick={this._putCursor}
        >
          <div className="topbar-search-input">
            <Input
              icon="search"
              inputRef={r => {
                this._searchInput = r
              }}
              className="e2-search"
              placeholder={
                this.props.simSearchEnabled
                  ? 'Search by keyword, YouTube URL or DISCO URL'
                  : 'Search DISCO'
              }
              value={this.state.searchTerm}
              onChange={this._handleInputChange}
              onFocus={this._handleInputFocus}
              onBlur={this._handleInputBlur}
            />
          </div>
        </div>
        {this._renderFilters()}
      </div>
    )
  }
}

export default SearchFilters
